import { useEffect, useRef } from 'react';
import { useMutation } from '@tanstack/react-query';
import { axiosGetV3 } from '../../api/axios-handler';
import { useCrisisModal } from '../../contexts/crisis-modal-context';
import type { Resource } from './useResources';

const CRISIS_CHECK_COOLDOWN_KEY = 'crisis_check_cooldown_until';
const CRISIS_CHECK_COOLDOWN_MS = 60 * 1000; // 1 minute

export const getCrisisCheckCooldownUntil = (): number | null => {
  const stored = sessionStorage.getItem(CRISIS_CHECK_COOLDOWN_KEY);
  if (stored == null) return null;
  const until = parseInt(stored, 10);
  return Number.isNaN(until) ? null : until;
};

export const isCrisisCheckInCooldown = (): boolean => {
  const until = getCrisisCheckCooldownUntil();
  return until != null && Date.now() < until;
};

export const setCrisisCheckCooldown = (): void => {
  sessionStorage.setItem(CRISIS_CHECK_COOLDOWN_KEY, String(Date.now() + CRISIS_CHECK_COOLDOWN_MS));
};

/** Crisis check without opening the global modal (e.g. Fi inline messaging). */
export const fetchCrisisCheck = async (query: string): Promise<CrisisCheckResponse> => {
  const response = await axiosGetV3('/simple_search/crisis_check', { q: query });
  return response.data;
};

export interface CrisisCheckResponse {
  crisis_detected: boolean;
  emergency: boolean;
  selfharm: boolean;
  crisis_resource?: Resource | null;
}

const checkCrisis = async (query: string): Promise<CrisisCheckResponse> => {
  const response = await axiosGetV3('/simple_search/crisis_check', { q: query });
  return response.data;
};

export const useCrisisCheck = () => {
  const { openCrisisModal } = useCrisisModal();

  return useMutation(checkCrisis, {
    onSuccess: (data) => {
      if (data.crisis_detected) {
        setCrisisCheckCooldown();
        openCrisisModal({
          emergency: data.emergency,
          selfHarm: data.selfharm,
          crisisResource: data.crisis_resource ?? undefined,
        });
      }
    },
  });
};

/**
 * Runs crisis check whenever a search query is provided/changed.
 * Call this from any hook that performs search (e.g. usePeersByTopic, useResourcesByTopic, useBrowseExperiences).
 */
export const useCrisisCheckOnSearchQuery = (searchQuery: string | undefined) => {
  const crisisCheck = useCrisisCheck();
  const lastCheckedQuery = useRef<string>('');

  const trimmed = searchQuery?.trim() || '';
  useEffect(() => {
    if (trimmed && trimmed !== lastCheckedQuery.current && !isCrisisCheckInCooldown()) {
      crisisCheck.mutate(trimmed);
      lastCheckedQuery.current = trimmed;
    }
  }, [trimmed]);
};
